import { useEffect, useState, useMemo } from "react";
import api, { money } from "@/lib/api";
import { Head, Loader, EmptyBox, Mini } from "./shared";
import { Receipt, DollarSign, AlertTriangle, Clock, CheckCircle2, ChevronRight } from "lucide-react";

const FILTERS = ["all", "overdue", "unpaid", "paid"];

export function Invoices({ onNav }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/invoices").then((r) => setData(r.data)).catch(() => setData({ invoices: [], summary: {} }));
  }, []);

  const invoices = data?.invoices || [];
  const s = data?.summary || {};

  const filtered = useMemo(() => invoices.filter((i) => {
    if (filter === "overdue" && !i.overdue) return false;
    if (filter === "unpaid" && i.status !== "unpaid") return false;
    if (filter === "paid" && i.status !== "paid") return false;
    if (q && !`${i.number || ""} ${i.org_name || ""} ${i.email || ""} ${i.description || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [invoices, filter, q]);

  const badge = (i) => i.overdue
    ? { cls: "bg-red-500/15 text-red-300 border-red-500/30", label: "Overdue", Icon: AlertTriangle }
    : i.status === "unpaid"
      ? { cls: "bg-gold/15 text-gold border-gold/30", label: "Unpaid", Icon: Clock }
      : { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Paid", Icon: CheckCircle2 };

  const openProject = (i) => {
    try { sessionStorage.setItem("admin_focus_project", i.project_id); } catch (e) {}
    onNav && onNav("projects");
  };

  if (!data) return <Loader />;
  return (
    <div>
      <Head title="Invoices" desc="Every invoice tied to a live project — issue, track and follow up on outstanding balances." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Mini label="Total invoices" value={s.total || 0} icon={Receipt} />
        <Mini label="Unpaid" value={s.unpaid || 0} icon={Clock} />
        <Mini label="Overdue" value={s.overdue || 0} icon={AlertTriangle} />
        <Mini label="Outstanding" value={money(s.outstanding || 0)} icon={DollarSign} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-navy-800 border border-white/10 p-1" data-testid="invoice-status-filter">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`invoice-filter-${f}`}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${filter === f ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
              {f}{f === "all" ? ` (${invoices.length})` : f === "overdue" ? ` (${s.overdue || 0})` : ` (${invoices.filter((i) => i.status === f).length})`}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoices…" data-testid="invoice-search"
          className="bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none flex-1 min-w-[180px]" />
      </div>

      {filtered.length === 0 ? <EmptyBox text="No invoices match your filters." /> : (
        <div className="bg-navy-800 border border-white/5 divide-y divide-white/5" data-testid="invoices-list">
          {filtered.map((i) => {
            const b = badge(i);
            return (
              <button key={i.id} onClick={() => openProject(i)} data-testid={`invoice-${i.id}`}
                className="w-full p-5 flex items-center justify-between gap-4 text-left hover:bg-white/5 transition-colors group">
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{i.number} · {i.org_name || i.email || "Client"}</div>
                  <div className="text-gray-500 text-xs truncate">
                    {i.description || i.kind || "Invoice"}
                    {i.due_date ? ` · due ${String(i.due_date).slice(0, 10)}` : ""}
                    {i.stage ? ` · ${i.stage}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-heading text-lg text-white">{money(i.amount)}</span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 border ${b.cls}`}><b.Icon size={12} /> {b.label}</span>
                  <ChevronRight size={15} className="text-gray-600 group-hover:text-gold transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
